import React, { Fragment } from 'react';
import { isEmpty } from 'lodash';

import {
  ACTIVE_ADVISORY,
  ACTIVE_PLUS,
  isActiveAdvisory
} from 'services/Products';
import Content, { contentConverter } from 'services/Content';
import { isUpdatedNovemberBenchmarksEnabled } from 'services/FeatureFlags';

export const rasBenchmarkStringToNumber = {
  ADVISORY_SERVICE_MODEL_100: 100,
  ADVISORY_SERVICE_MODEL_90: 90,
  ADVISORY_SERVICE_MODEL_80: 80,
  ADVISORY_SERVICE_MODEL_70: 70,
  ADVISORY_SERVICE_MODEL_60: 60,
  ADVISORY_SERVICE_MODEL_50: 50,
  ADVISORY_SERVICE_MODEL_40: 40,
  ADVISORY_SERVICE_MODEL_30: 30,
  ADVISORY_SERVICE_MODEL_20: 20,
  ADVISORY_SERVICE_MODEL_10: 10
};

export const appBenchmarkTooltipStringToNumber = (accountName) => {
  return isEmpty(accountName) ? 0 : parseInt(accountName.replace(/\D/g, ''));
};

export const DOMESTIC_STOCKS_ALL_CAP = 'Domestic Stocks - All Cap';
export const INTERNATIONAL_STOCKS_DEVELOPED_EMERGING =
  'International Stocks - Developed and Emerging Markets';
export const INTERNATIONAL_STOCKS_DEVELOPED =
  'International Stocks - Developed Markets';
export const DOMESTIC_BONDS_INVESTMENT_GRADE =
  'Domestic Bonds - Investment Grade';
export const DOMESTIC_BONDS_SHORT_TERM_GRADE =
  'Domestic Bonds - Short-Term Investment Grade';
export const DOMESTIC_BONDS_INFLATION_LINKED =
  'Domestic Bonds - Inflation Linked';
export const DOMESTIC_STOCKS_LARGE_CAP = 'Domestic Stocks - Large Cap';
export const SHORT_TERM_SECURITIES = 'Short Term Securities - Cash Equivalents';

// Using the arg here instead of the feature flag function as unit testing did not seem to mock the latter correctly outside the component.
const determineDomesticBondsIndex = (useUpdatedIndices = false) => {
  return useUpdatedIndices
    ? DOMESTIC_BONDS_INFLATION_LINKED
    : DOMESTIC_BONDS_SHORT_TERM_GRADE;
};

export const modelBenchmarkModels = (useUpdatedIndices = false) => ({
  [ACTIVE_PLUS]: {
    100: [
      [70, DOMESTIC_STOCKS_ALL_CAP],
      [30, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING]
    ],
    90: [
      [63, DOMESTIC_STOCKS_ALL_CAP],
      [27, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [10, DOMESTIC_BONDS_INVESTMENT_GRADE]
    ],
    80: [
      [56, DOMESTIC_STOCKS_ALL_CAP],
      [24, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [20, DOMESTIC_BONDS_INVESTMENT_GRADE]
    ],
    70: [
      [49, DOMESTIC_STOCKS_ALL_CAP],
      [21, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [27, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [3, DOMESTIC_BONDS_INFLATION_LINKED]
    ],
    60: [
      [42, DOMESTIC_STOCKS_ALL_CAP],
      [18, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [33, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [7, DOMESTIC_BONDS_INFLATION_LINKED]
    ],
    50: [
      [35, DOMESTIC_STOCKS_ALL_CAP],
      [15, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [38, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [12, DOMESTIC_BONDS_INFLATION_LINKED]
    ],
    40: [
      [28, DOMESTIC_STOCKS_ALL_CAP],
      [12, INTERNATIONAL_STOCKS_DEVELOPED],
      [43, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [17, DOMESTIC_BONDS_INFLATION_LINKED]
    ],
    30: [
      [21, DOMESTIC_STOCKS_ALL_CAP],
      [9, INTERNATIONAL_STOCKS_DEVELOPED],
      [50, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [20, DOMESTIC_BONDS_INFLATION_LINKED]
    ],
    20: [
      [14, DOMESTIC_STOCKS_LARGE_CAP],
      [6, INTERNATIONAL_STOCKS_DEVELOPED],
      [60, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [20, DOMESTIC_BONDS_INFLATION_LINKED]
    ],
    10: [
      [7, DOMESTIC_STOCKS_LARGE_CAP],
      [3, INTERNATIONAL_STOCKS_DEVELOPED],
      [60, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [20, DOMESTIC_BONDS_INFLATION_LINKED],
      [10, SHORT_TERM_SECURITIES]
    ]
  },
  [ACTIVE_ADVISORY]: {
    100: [
      [70, DOMESTIC_STOCKS_ALL_CAP],
      [30, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING]
    ],
    90: [
      [63, DOMESTIC_STOCKS_ALL_CAP],
      [27, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [10, DOMESTIC_BONDS_INVESTMENT_GRADE]
    ],
    80: [
      [56, DOMESTIC_STOCKS_ALL_CAP],
      [24, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [20, DOMESTIC_BONDS_INVESTMENT_GRADE]
    ],
    70: [
      [49, DOMESTIC_STOCKS_ALL_CAP],
      [21, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [30, DOMESTIC_BONDS_INVESTMENT_GRADE]
    ],
    60: [
      [42, DOMESTIC_STOCKS_ALL_CAP],
      [18, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [40, DOMESTIC_BONDS_INVESTMENT_GRADE]
    ],
    50: [
      [35, DOMESTIC_STOCKS_ALL_CAP],
      [15, INTERNATIONAL_STOCKS_DEVELOPED_EMERGING],
      [44, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [6, determineDomesticBondsIndex(useUpdatedIndices)]
    ],
    40: [
      [28, DOMESTIC_STOCKS_LARGE_CAP],
      [12, INTERNATIONAL_STOCKS_DEVELOPED],
      [50, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [10, determineDomesticBondsIndex(useUpdatedIndices)]
    ],
    30: [
      [21, DOMESTIC_STOCKS_LARGE_CAP],
      [9, INTERNATIONAL_STOCKS_DEVELOPED],
      [60, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [10, determineDomesticBondsIndex(useUpdatedIndices)]
    ],
    20: [
      [14, DOMESTIC_STOCKS_LARGE_CAP],
      [6, INTERNATIONAL_STOCKS_DEVELOPED],
      [65, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [15, determineDomesticBondsIndex(useUpdatedIndices)]
    ],
    10: [
      [10, DOMESTIC_STOCKS_LARGE_CAP],
      [70, DOMESTIC_BONDS_INVESTMENT_GRADE],
      [20, determineDomesticBondsIndex(useUpdatedIndices)]
    ]
  }
});

// Displays under models which have had their benchmarks updated.
const updatedBenchmarksCopies = {
  10: [
    'Effective November 4th, 2024, the model changed its primary benchmark from 20% Domestic Bonds – Short-Term Investment Grade ' +
      'to 20% Domestic Bonds – Inflation Linked. Historical benchmark representations have been restated.'
  ],
  20: [
    'Effective November 4th, 2024, the model changed its primary benchmark from 15% Domestic Bonds – Short-Term Investment Grade ' +
      'to 15% Domestic Bonds – Inflation Linked. Historical benchmark representations have been restated.'
  ],
  30: [
    'Effective November 4th, 2024, the model changed its primary benchmark from 10% Domestic Bonds – Short-Term Investment Grade ' +
      'to 10% Domestic Bonds – Inflation Linked. Historical benchmark representations have been restated.'
  ],
  40: [
    'Effective November 4th, 2024, the model changed its primary benchmark from 10% Domestic Bonds – Short-Term Investment Grade ' +
      'to 10% Domestic Bonds – Inflation Linked. Historical benchmark representations have been restated.'
  ],
  50: [
    'Effective August 23rd, 2023, the model changed its primary benchmark from 50% Domestic Bonds – Investment Grade ' +
      'to 44% Domestic Bonds – Investment Grade and 6% Domestic Bonds – Short-Term Investment Grade. Historical benchmark representations have not been restated.',
    'Effective November 4th, 2024, the model changed its primary benchmark from 6% Domestic Bonds – Short-Term Investment Grade ' +
      'to 6% Domestic Bonds – Inflation Linked. Historical benchmark representations have been restated.'
  ]
};

const rasComprises = (modelNumber) =>
  `The Retirement Advisory Service Model ${modelNumber} benchmark is comprised of: `;
const appComprises = (modelNumber) =>
  `The ActivePlus Portfolio® - Retirement ${modelNumber} model benchmark is comprised of: `;

/*
Takes in an array of tuples corresponding to the modelBenchmarkModels.
 */
const ModelList = ({ modelData }) => {
  return (
    <ul className="index-list">
      {modelData.map(([percent, modelIndex], index) => {
        return <li key={index}>{`${percent}% ${modelIndex}`}</li>;
      })}
    </ul>
  );
};

export const BenchmarkTooltipBody = ({ productType, modelNumber = 0 }) => {
  const useUpdatedIndices = isUpdatedNovemberBenchmarksEnabled();
  const comprises = isActiveAdvisory(productType)
    ? rasComprises(modelNumber)
    : appComprises(modelNumber);
  const percentages =
    modelBenchmarkModels(useUpdatedIndices)[productType][modelNumber] || [];
  const updatedBenchmarksCopy = updatedBenchmarksCopies[modelNumber] ?? '';

  let indexDefinitions;
  if (isActiveAdvisory(productType)) {
    if (useUpdatedIndices) {
      indexDefinitions = Content.updatedModelBenchmarkRasIndexDefinitions;
    } else {
      indexDefinitions = Content.modelBenchmarkRasIndexDefinitions;
    }
  } else {
    indexDefinitions = Content.modelBenchmarkAppIndexDefinitions;
  }

  return (
    <div className="benchmark-modal-content">
      <div className="index-content">
        {isEmpty(percentages) ? (
          <p>Data cannot be shown at this time.</p>
        ) : (
          <Fragment>
            <p className="comprises-subtitle">{comprises}</p>
            <ModelList modelData={percentages} />
            <p className="update-content">
              When calculating the performance of the model benchmark, the
              weightings of the underlying indices are rebalanced monthly.
            </p>
            {useUpdatedIndices &&
              updatedBenchmarksCopy &&
              updatedBenchmarksCopy.map((copy, index) => (
                <p key={index} className="update-content">
                  {copy}
                </p>
              ))}
            <div className="definitions-content">
              <p className="definitions-subtitle">Index Definitions</p>
              <div
                className="definitions-list"
                dangerouslySetInnerHTML={{
                  __html: contentConverter.makeHtml(indexDefinitions)
                }}
              />
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
};
